import React from "react";
import { PlaySignals, SuppressedCount } from "../../../api/playAdminApi";
import { SuppressedCountValue, numericOrZero } from "./SuppressedCountValue";

const Card: React.FC<{
  title: string;
  testId: string;
  children: React.ReactNode;
}> = ({ title, testId, children }) => (
  <div
    className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
    data-testid={testId}
  >
    <h3 className="text-sm font-medium text-gray-500">{title}</h3>
    <div className="mt-2">{children}</div>
  </div>
);

/** Bespoke Tailwind bar rows (no chart lib). Suppressed rows get NO bar. */
const BarList: React.FC<{
  rows: Array<{ label: string; value: SuppressedCount }>;
  emptyText: string;
}> = ({ rows, emptyText }) => {
  if (rows.length === 0) {
    return <p className="text-sm text-gray-400">{emptyText}</p>;
  }
  const max = Math.max(1, ...rows.map((r) => numericOrZero(r.value)));
  return (
    <ul className="space-y-2">
      {rows.map((row) => (
        <li key={row.label} className="text-sm">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-gray-700">{row.label}</span>
            <SuppressedCountValue value={row.value} />
          </div>
          {typeof row.value === "number" && (
            <div className="mt-1 h-1.5 rounded bg-gray-100">
              <div
                className="h-1.5 rounded bg-indigo-500"
                style={{ width: `${(numericOrZero(row.value) / max) * 100}%` }}
              />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};

export const CompletionsByFamilyCard: React.FC<{
  data: PlaySignals["completionsByFamily"];
}> = ({ data }) => (
  <Card title="Completions by focus area" testId="block-completions">
    <BarList
      rows={data.map((d) => ({ label: d.family, value: d.completions }))}
      emptyText="No completed practice runs in this period."
    />
  </Card>
);

export const EscalationCountCard: React.FC<{ value: SuppressedCount }> = ({
  value,
}) => (
  <Card title="Safety escalations" testId="block-escalations">
    <p className="text-2xl font-semibold text-gray-900">
      <SuppressedCountValue value={value} />
    </p>
    <p className="mt-1 text-xs text-gray-500">
      Escalating to a person is a successful, professional outcome.
    </p>
  </Card>
);

export const RiskFlagsCard: React.FC<{
  data: PlaySignals["commonRiskFlags"];
}> = ({ data }) => (
  <Card title="Common safety flags" testId="block-risk-flags">
    <BarList
      rows={data.map((d) => ({ label: d.category, value: d.occurrences }))}
      emptyText="No safety flags in this period."
    />
  </Card>
);

export const OversightRatioCard: React.FC<{ value: number | null }> = ({
  value,
}) => (
  <Card title="Human oversight ratio" testId="block-oversight">
    {value === null ? (
      <p className="text-sm text-gray-400" data-testid="oversight-empty">
        Not enough data yet
      </p>
    ) : (
      <>
        <p className="text-2xl font-semibold text-gray-900">
          {Math.round(value * 100)}%
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Share of AI drafts actively reviewed with edits (or rejected) rather
          than approved as-is.
        </p>
      </>
    )}
  </Card>
);

export const ReviewQualityCard: React.FC<{
  data: PlaySignals["reviewQualityDistribution"];
}> = ({ data }) => (
  <Card title="Review quality (1–5)" testId="block-review-quality">
    <BarList
      rows={data.map((d) => ({ label: `${d.rating}★`, value: d.count }))}
      emptyText="No quality ratings in this period."
    />
  </Card>
);

export const EvidenceCandidateCard: React.FC<{ value: SuppressedCount }> = ({
  value,
}) => (
  <Card title="Evidence candidates" testId="block-evidence-candidates">
    <p className="text-2xl font-semibold text-gray-900">
      <SuppressedCountValue value={value} />
    </p>
  </Card>
);

export const ReadinessMovementCard: React.FC<{
  data: PlaySignals["readinessMovement"];
}> = ({ data }) => (
  <Card title="Readiness movement" testId="block-readiness">
    <div className="flex items-baseline gap-4 text-sm text-gray-700">
      <span>
        This period:{" "}
        <SuppressedCountValue value={data.currentPeriodEvidence} />
      </span>
      <span>
        Previous:{" "}
        <SuppressedCountValue value={data.previousPeriodEvidence} />
      </span>
    </div>
  </Card>
);

export const UsageCostTable: React.FC<{
  data: PlaySignals["usageCostByFamily"];
}> = ({ data }) => (
  <Card title="AI usage & cost by workflow" testId="block-usage-cost">
    {data.length === 0 ? (
      <p className="text-sm text-gray-400">No AI usage in this period.</p>
    ) : (
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500">
            <th className="py-1">Workflow task</th>
            <th className="py-1 text-right">Calls</th>
            <th className="py-1 text-right">Tokens (in/out)</th>
            <th className="py-1 text-right">Cost</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.feature} className="border-t border-gray-100">
              <td className="py-1.5 text-gray-700">{row.feature}</td>
              <td className="py-1.5 text-right">
                <SuppressedCountValue value={row.calls} />
              </td>
              <td className="py-1.5 text-right text-gray-600">
                {/* Suppressed rows mask token/cost cells — em-dash, no math. */}
                {row.promptTokens === null || row.completionTokens === null
                  ? "—"
                  : `${row.promptTokens}/${row.completionTokens}`}
              </td>
              <td className="py-1.5 text-right text-gray-600">
                {row.costCents === null
                  ? "—"
                  : `£${(row.costCents / 100).toFixed(2)}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </Card>
);

export const AllowanceCard: React.FC<{ data: PlaySignals["allowance"] }> = ({
  data,
}) => (
  <Card title="Practice allowance (this month)" testId="block-allowance">
    <p className="text-2xl font-semibold text-gray-900">
      {data.used}
      <span className="text-base font-normal text-gray-500">
        {" "}
        / {data.limit} drafts
      </span>
    </p>
    <p className="mt-1 text-xs text-gray-500">
      Resets {new Date(data.resetsAt).toLocaleDateString()} · limit source:{" "}
      {data.source}
    </p>
  </Card>
);
